import type { RumEvent } from '@datadog/browser-rum'

/**
 * What replaces a credential's value.
 *
 * The parameter's **key survives** the substitution on purpose. A reader who
 * sees `?token=REDACTED` learns that a credential was sent and scrubbed;
 * deleting the key instead would make an event that carried one
 * indistinguishable from an event that never did, which is the more expensive
 * kind of missing information.
 */
export const REDACTED_PARAM_VALUE = 'REDACTED'

/**
 * Query-parameter names whose value is treated as a credential.
 *
 * Matched as a **substring** of the name and **case-insensitively**. A
 * substring rather than an exact list because the same value already travels
 * under more than one spelling: `services/postCreateOrder.tsx` sends it as
 * `?token=` to the workflow webhook and as `?jwt=` to botrunner, and reads it
 * off the storefront config under a third name (`jwtToken`). `access_token`,
 * `id_token` or `authToken` are therefore one endpoint rename away, and an
 * exact-name list would go stale without anything failing. Case-insensitively
 * because the accepting server decides the casing, and over-matching a
 * parameter literally named `Token` costs nothing.
 *
 * Kept to the two names for which a carrier actually exists, because this is a
 * denylist applied to `view.url` as well as to request URLs and the URL is
 * otherwise the most useful thing on the event. Cinnamon's own parameters
 * (`itemListName`, `referrer`, `status`) and every path segment are left
 * intact, which is the point — a heuristic broad enough to catch any
 * conceivable credential would take the diagnostic value of the URL with it.
 * Widen this on the evidence of a new carrier, not in anticipation of one.
 */
const CREDENTIAL_PARAM_NAMES = ['token', 'jwt']

/*
 * Anchored on a `?`, `&` or `#` and stopped at the following `=`, so only a
 * parameter *name* can match: `?a=token=x` names `a` and is left alone, and a
 * value that happens to contain the word is never mistaken for a name.
 *
 * `[^&#]+` for the value — `+` rather than `*` — so an empty parameter stays
 * empty. `?token=` is already carrying nothing, and rewriting it to
 * `?token=REDACTED` would assert that a value was transmitted.
 * The value class deliberately admits `=`, which JWTs and base64 padding both
 * contain.
 */
const CREDENTIAL_PARAM_PATTERN = new RegExp(
  `([?&#][^?&#=]*(?:${CREDENTIAL_PARAM_NAMES.join('|')})[^?&#=]*=)[^&#]+`,
  'gi',
)

/**
 * Replaces the value of every credential-named parameter in a URL and leaves
 * the rest of the string byte-for-byte intact.
 *
 * **Deliberately not `new URL()`.** Parsing would give a tidier substitution
 * but buys two problems this string pass does not have. It throws on relative
 * and malformed input — and a `beforeSend` that throws takes RUM down for the
 * whole session — so it would need a fallback, and any fallback that leaves the
 * value unscrubbed defeats the purpose while one that discards the field costs
 * observability. And re-serialising through `URLSearchParams` re-encodes the
 * parameters that were *not* redacted, so the surviving URL would no longer be
 * the one the browser requested. A delimiter-anchored substitution has no
 * unparseable input class at all: an absolute URL, a relative path, a bare
 * fragment and an outright malformed string all take the same code path, and
 * `String.prototype.replace` on a string cannot throw.
 *
 * Safe to call with a module-level `/g` regex: `replace` resets `lastIndex`,
 * unlike `test` and `exec`.
 */
export const redactCredentialParams = (url: string): string =>
  url.replace(CREDENTIAL_PARAM_PATTERN, `$1${REDACTED_PARAM_VALUE}`)

/**
 * Strips credentials out of every URL field a RUM event can carry, in place.
 *
 * Wired as `datadogRum.init`'s `beforeSend` in `App.tsx`, which is where the
 * reason for the whole thing is written down (COM-855). Returns `void` rather
 * than a boolean so the event is always **kept**: `beforeSend` returning
 * `false` drops the event, and dropping is a loss of observability where
 * redacting is not. Nothing in here can change that — the function has no
 * `return` statement and no branch that produces a value.
 *
 * **The five fields are the complete set of URL-bearing paths the SDK lets a
 * client rewrite**, not a selection from it:
 * `@datadog/browser-rum-core@4.50.1`'s `domain/assembly.js` copies a
 * `beforeSend` mutation back onto the event only for the paths in
 * `modifiableFieldPathsByEvent`, and the other string paths it does list
 * (`error.message`, `error.stack`, `error.fingerprint`, `action.target.name`)
 * are not URL fields. The first two are free text that could in principle
 * embed one, and are left alone: no error message *constructed in this repo*
 * is built from a URL — `src` holds no template-literal `new Error` at all,
 * and no `new Error` naming a url, api or http value — and applying a URL rule
 * to prose is a blunter instrument than it looks.
 * Three of the five ride on **every** event type, which is why
 * scrubbing `resource.url` alone would have left errors, actions, views and
 * long tasks exposed:
 * - `view.url` — `location.href`, stamped on every event
 *   (`domain/contexts/urlContexts.js`).
 * - `view.referrer` — `document.referrer` for the first view, the previous
 *   view's URL afterwards, so it inherits whatever `view.url` carried.
 * - `view.name` — every `datadogRum.startView` call in this repo passes
 *   `generateDataDogContext`'s `viewName`, and that is `pathname + search`. A
 *   live carrier of the webview's own query string, not a hypothetical one.
 * - `resource.url` — the field COM-855 is about, and the only one that
 *   demonstrably carries the workflow JWT today.
 * - `error.resource.url` — **defensive.** It is on the SDK's modifiable list
 *   and on the server-side event type, but nothing populates it: no installed
 *   `@datadog` package contains a `trackNetworkError`, `RawRumErrorEvent` in
 *   `rawRumEvent.types.d.ts` declares no `resource` member at all, and
 *   `@datadog/browser-logs` is an optional peer dependency that is not
 *   installed. So in 4.50.1 this branch cannot fire. Read it as cover for a
 *   version bump, not as a measured population.
 *
 * **What it does not cover, so that nobody has to guess.** By choice: the
 * event's `context`, which `assembly.js` *does* allow a `beforeSend` to
 * rewrite, and into which `utils/dataDog.ts` puts both `searchParams` (every
 * query parameter of the webview URL, by name) and `viewName` on most
 * `addError` calls. Covering that needs a policy on arbitrary nested customer
 * context rather than a URL rule, and a partial cover would be worse than
 * none because it would read as a full one. Nothing there carries a
 * credential today — no cinnamon URL parameter is one.
 * Not by choice: anything absent from `modifiableFieldPathsByEvent` is
 * unrewritable from here, whatever a `beforeSend` does to it —
 * `error.handling_stack`, which `rumPublicApi.js`'s `addError` fills from
 * `createHandlingStack()` on every public call, is the notable one, along with
 * `error.causes` and `error.type`. A `beforeSend` cannot redact those; keeping
 * a credential out of them is the caller's job.
 */
export const redactRumEventUrls = (event: RumEvent): void => {
  /*
   * Guarded per field rather than wrapped in a `try`. Every access below is
   * optional-chained and every write is behind a `typeof` test, so no input
   * shape is left that could throw — and a `catch` here would silently ship the
   * unscrubbed event, which is the failure this function exists to prevent.
   */
  if (typeof event.view?.url === 'string') {
    event.view.url = redactCredentialParams(event.view.url)
  }
  if (typeof event.view?.referrer === 'string') {
    event.view.referrer = redactCredentialParams(event.view.referrer)
  }
  if (typeof event.view?.name === 'string') {
    event.view.name = redactCredentialParams(event.view.name)
  }
  if (event.type === 'resource' && typeof event.resource?.url === 'string') {
    event.resource.url = redactCredentialParams(event.resource.url)
  }
  if (event.type === 'error') {
    const errorResource = event.error?.resource
    if (errorResource && typeof errorResource.url === 'string') {
      errorResource.url = redactCredentialParams(errorResource.url)
    }
  }
}
